import React, { useMemo } from 'react';
import { Plus } from 'lucide-react';
import { CHILE_MOBILE_PLACEHOLDER, PHONE_MAX_LENGTH, formatChileanMobile } from '../../utils/textUtils';
import { partitionQuotationLineItems } from '../placeholders/quotationItemClassification';

const DELIVERY_METHOD_OPTIONS = [
  { value: 'Despacho', label: 'Despacho' },
  { value: 'Retiro en sucursal', label: 'Retiro en sucursal' },
];

const BILLING_DOCUMENT_OPTIONS = [
  { value: 'Boleta electrónica', label: 'Boleta electrónica' },
  { value: 'Factura', label: 'Factura' },
];

const sectionTitle = (text) => (
  <div style={{ fontSize: '12px', fontWeight: 800, color: '#0f766e', letterSpacing: '0.02em', textTransform: 'uppercase' }}>
    {text}
  </div>
);

const fieldLabel = (text, muted) => (
  <span style={{ fontSize: '12px', fontWeight: 600, color: muted }}>{text}</span>
);

/** Definido a nivel de módulo para no remountar inputs en cada tecla. */
const Field = ({ label, muted, children, span = 1 }) => (
  <label
    style={{
      display: 'flex',
      flexDirection: 'column',
      gap: '6px',
      minWidth: 0,
      gridColumn: span > 1 ? `span ${span}` : undefined,
    }}
  >
    {fieldLabel(label, muted)}
    {children}
  </label>
);

const QuotationVendidaCloseWizard = ({
  ui,
  quotation,
  lineItems,
  users = [],
  paintSkuSet,
  upsellingSkuSet,
  vendidaWizardStep,
  closingDate,
  setClosingDate,
  maxClosingDate,
  orderCode,
  setOrderCode,
  responsibleUserId,
  setResponsibleUserId,
  buyerContactId,
  setBuyerContactId,
  buyerPhone,
  setBuyerPhone,
  buyerEmail,
  setBuyerEmail,
  hasPurchaseOrder,
  setHasPurchaseOrder,
  poCode,
  setPoCode,
  poDate,
  setPoDate,
  poAcceptedDate,
  setPoAcceptedDate,
  poAmount,
  setPoAmount,
  deliveryMethod,
  setDeliveryMethod,
  deliveryAddress,
  setDeliveryAddress,
  dispatchDate,
  setDispatchDate,
  scheduledDeliveryCode,
  setScheduledDeliveryCode,
  deliveryCodeError = '',
  expectedDeliveryDate,
  billingDocumentType,
  setBillingDocumentType,
  billingAddress,
  setBillingAddress,
  billingGiro,
  setBillingGiro,
  organization,
  orgContacts,
  addressOptions,
  productionSelections,
  updateProductionSelection,
  productionNotes = '',
  setProductionNotes,
  upsellingSelections,
  updateUpsellingSelection,
  showNewContact,
  setShowNewContact,
  newContactName,
  setNewContactName,
  newContactEmail,
  setNewContactEmail,
  newContactPhone,
  setNewContactPhone,
  onSaveNewContact,
  showNewDeliveryAddress,
  setShowNewDeliveryAddress,
  showNewBillingAddress,
  setShowNewBillingAddress,
  setNewAddressTarget,
  newAddressText,
  setNewAddressText,
  onSaveNewAddress,
  savingExtra,
  isClosing,
  inputStyle,
  btnBase,
  linkBtn,
  formatMoney,
}) => {
  const grid3 = {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: '10px',
  };
  const grid4 = {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: '10px',
  };

  const plusButtonStyle = {
    flexShrink: 0,
    width: '34px',
    height: '34px',
    borderRadius: '8px',
    border: `1px solid ${ui.border}`,
    background: ui.panel,
    color: ui.text,
    cursor: isClosing ? 'not-allowed' : 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    opacity: isClosing ? 0.75 : 1,
  };

  const { production, upselling } = useMemo(
    () => partitionQuotationLineItems(lineItems, paintSkuSet, upsellingSkuSet),
    [lineItems, paintSkuSet, upsellingSkuSet]
  );

  const renderItemSelectionTable = (items, selectionMap, onUpdate, emptyLabel) => {
    if (!items.length) {
      return <div style={{ fontSize: '12px', color: ui.muted }}>{emptyLabel}</div>;
    }
    return (
      <div style={{ overflowX: 'auto', border: `1px solid ${ui.border}`, borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
          <thead>
            <tr style={{ background: ui.header }}>
              {['', 'Producto', 'Unidad', 'Cant. cotizada', 'Cant. a solicitar'].map((h, idx) => (
                <th
                  key={h || 'check'}
                  style={{
                    padding: '8px 10px',
                    textAlign: idx >= 3 ? 'right' : 'left',
                    fontSize: '11px',
                    color: ui.muted,
                    borderBottom: `1px solid ${ui.border}`,
                    width: idx === 0 ? '40px' : undefined,
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {items.map((item) => {
              const itemId = Number(item.id);
              const row = selectionMap[itemId] || { enabled: false, quantity: '' };
              return (
                <tr key={item.id || itemId}>
                  <td style={{ padding: '8px 10px', borderBottom: `1px solid ${ui.border}` }}>
                    <input
                      type="checkbox"
                      checked={Boolean(row.enabled)}
                      onChange={(e) => onUpdate(itemId, 'enabled', e.target.checked)}
                      disabled={isClosing}
                    />
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: '12px', color: ui.text, borderBottom: `1px solid ${ui.border}` }}>
                    <div style={{ fontWeight: 600 }}>{item.item_name || item.product_name || '—'}</div>
                    <div style={{ fontSize: '11px', color: ui.muted }}>{item.product_sku || item.item_code || '—'}</div>
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: '12px', color: ui.muted, borderBottom: `1px solid ${ui.border}` }}>
                    {item.unit || '—'}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: '12px', textAlign: 'right', borderBottom: `1px solid ${ui.border}` }}>
                    {Number(item.quantity || 0).toLocaleString('es-CL')}
                  </td>
                  <td style={{ padding: '8px 10px', borderBottom: `1px solid ${ui.border}` }}>
                    <input
                      type="number"
                      min="0"
                      step="any"
                      value={row.quantity ?? ''}
                      onChange={(e) => onUpdate(itemId, 'quantity', e.target.value)}
                      disabled={isClosing || !row.enabled}
                      style={{ ...inputStyle, width: '100%', textAlign: 'right' }}
                    />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  };

  const renderStep1 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: ui.muted }}>
        Paso 1 de 3 — Información general, OC y facturación
      </div>

      {sectionTitle('Información general')}
      <div style={grid3}>
        <Field muted={ui.muted} label="Fecha de cierre">
          <input
            type="date"
            value={closingDate}
            max={maxClosingDate}
            onChange={(e) => setClosingDate(e.target.value)}
            disabled={isClosing}
            style={inputStyle}
          />
        </Field>
        <Field muted={ui.muted} label="Código de venta">
          <input
            type="text"
            value={orderCode}
            onChange={(e) => setOrderCode(e.target.value)}
            disabled={isClosing}
            autoComplete="off"
            style={inputStyle}
          />
        </Field>
        <Field muted={ui.muted} label="Ejecutivo responsable">
          <select
            value={responsibleUserId}
            onChange={(e) => setResponsibleUserId(e.target.value)}
            disabled={isClosing}
            style={inputStyle}
          >
            <option value="">Seleccionar ejecutivo…</option>
            {(users || []).map((user) => (
              <option key={user.id} value={String(user.id)}>
                {user.full_name || user.name || `Usuario ${user.id}`}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div style={grid3}>
        <Field muted={ui.muted} label="Nombre de contacto">
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <select
              value={buyerContactId}
              onChange={(e) => setBuyerContactId(e.target.value)}
              disabled={isClosing}
              style={{ ...inputStyle, flex: 1, minWidth: 0 }}
            >
              <option value="">Sin contacto</option>
              {orgContacts.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {c.full_name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setShowNewContact((v) => !v)}
              disabled={isClosing}
              title="Nuevo contacto"
              aria-label="Nuevo contacto"
              style={plusButtonStyle}
            >
              <Plus size={16} />
            </button>
          </div>
        </Field>
        <Field muted={ui.muted} label="Teléfono de contacto">
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            maxLength={PHONE_MAX_LENGTH}
            placeholder={CHILE_MOBILE_PLACEHOLDER}
            value={formatChileanMobile(buyerPhone)}
            onChange={(e) => setBuyerPhone(formatChileanMobile(e.target.value))}
            disabled={isClosing}
            style={inputStyle}
          />
        </Field>
        <Field muted={ui.muted} label="Correo de contacto">
          <input
            type="email"
            value={buyerEmail}
            onChange={(e) => setBuyerEmail(e.target.value)}
            disabled={isClosing}
            autoComplete="email"
            style={inputStyle}
          />
        </Field>
      </div>

      {showNewContact ? (
        <div style={grid4}>
          <input style={inputStyle} placeholder="Nombre" value={newContactName} onChange={(e) => setNewContactName(e.target.value)} />
          <input style={inputStyle} placeholder="Email" value={newContactEmail} onChange={(e) => setNewContactEmail(e.target.value)} />
          <input style={inputStyle} type="tel" placeholder={CHILE_MOBILE_PLACEHOLDER} value={formatChileanMobile(newContactPhone)} onChange={(e) => setNewContactPhone(formatChileanMobile(e.target.value))} />
          <button type="button" disabled={savingExtra} onClick={onSaveNewContact} style={{ ...btnBase, border: 'none', background: '#2563eb', color: '#fff' }}>
            {savingExtra ? 'Guardando…' : 'Guardar contacto'}
          </button>
        </div>
      ) : null}

      <div style={grid3}>
        <Field muted={ui.muted} label="Tipo de entrega">
          <select
            value={deliveryMethod}
            onChange={(e) => {
              const nextMethod = e.target.value;
              setDeliveryMethod(nextMethod);
              if (nextMethod === 'Retiro en sucursal') setDeliveryAddress('');
              else if (!String(deliveryAddress || '').trim() && addressOptions.length) {
                const fromQuote = String(quotation?.delivery_place || '').trim();
                setDeliveryAddress(fromQuote || addressOptions[0].value);
              }
            }}
            disabled={isClosing}
            style={inputStyle}
          >
            {DELIVERY_METHOD_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Field>
        <Field muted={ui.muted} label="Dirección de entrega" span={2}>
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
            <select
              value={deliveryMethod === 'Retiro en sucursal' ? '' : deliveryAddress}
              onChange={(e) => setDeliveryAddress(e.target.value)}
              disabled={isClosing || deliveryMethod === 'Retiro en sucursal'}
              style={{
                ...inputStyle,
                flex: 1,
                minWidth: 0,
                backgroundColor: deliveryMethod === 'Retiro en sucursal' ? ui.sectionBg : undefined,
                opacity: deliveryMethod === 'Retiro en sucursal' ? 0.7 : 1,
                cursor: deliveryMethod === 'Retiro en sucursal' ? 'not-allowed' : 'pointer',
              }}
            >
              <option value="">{deliveryMethod === 'Retiro en sucursal' ? 'No aplica en retiro en sucursal' : 'Seleccionar dirección…'}</option>
              {deliveryAddress && !addressOptions.some((a) => a.value === deliveryAddress) ? (
                <option value={deliveryAddress}>{deliveryAddress}</option>
              ) : null}
              {addressOptions.map((a) => (
                <option key={a.value} value={a.value}>{a.label}</option>
              ))}
            </select>
            {deliveryMethod === 'Despacho' ? (
              <button
                type="button"
                onClick={() => {
                  setNewAddressTarget('delivery');
                  setShowNewDeliveryAddress((v) => !v);
                  setShowNewBillingAddress(false);
                }}
                disabled={isClosing}
                title="Nueva dirección de entrega"
                aria-label="Nueva dirección de entrega"
                style={plusButtonStyle}
              >
                <Plus size={16} />
              </button>
            ) : null}
          </div>
        </Field>
      </div>

      {showNewDeliveryAddress && deliveryMethod === 'Despacho' ? (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <input style={{ ...inputStyle, flex: 1 }} placeholder="Nueva dirección" value={newAddressText} onChange={(e) => setNewAddressText(e.target.value)} />
          <button type="button" disabled={savingExtra} onClick={onSaveNewAddress} style={{ ...btnBase, border: 'none', background: '#2563eb', color: '#fff' }}>Guardar</button>
        </div>
      ) : null}

      {sectionTitle('Orden de compra')}
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '10px 12px',
          borderRadius: '8px',
          border: `1px solid ${ui.border}`,
          background: ui.sectionBg,
          cursor: isClosing ? 'default' : 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={hasPurchaseOrder}
          onChange={(e) => setHasPurchaseOrder(e.target.checked)}
          disabled={isClosing}
          style={{ width: 16, height: 16, accentColor: '#0f766e' }}
        />
        <span style={{ fontSize: '13px', fontWeight: 600, color: ui.text }}>
          La venta tiene orden de compra (OC)
        </span>
      </label>
      {hasPurchaseOrder ? (
        <div style={grid4}>
          <Field muted={ui.muted} label="Código OC">
            <input type="text" value={poCode} onChange={(e) => setPoCode(e.target.value)} disabled={isClosing} style={inputStyle} />
          </Field>
          <Field muted={ui.muted} label="Fecha recepción OC">
            <input type="date" value={poDate} onChange={(e) => setPoDate(e.target.value)} disabled={isClosing} style={inputStyle} />
          </Field>
          <Field muted={ui.muted} label="Fecha aceptación OC">
            <input type="date" value={poAcceptedDate} onChange={(e) => setPoAcceptedDate(e.target.value)} disabled={isClosing} style={inputStyle} />
          </Field>
          <Field muted={ui.muted} label="Monto OC">
            <input type="number" min="0" step="1" value={poAmount} onChange={(e) => setPoAmount(e.target.value)} disabled={isClosing} style={inputStyle} />
          </Field>
        </div>
      ) : null}

      {sectionTitle('Facturación')}
      <div style={grid3}>
        <Field muted={ui.muted} label="Documento tributario">
          <select
            value={billingDocumentType}
            onChange={(e) => {
              const nextType = e.target.value;
              setBillingDocumentType(nextType);
              if (nextType !== 'Factura') {
                setBillingAddress('');
                setBillingGiro('');
              } else if (!billingGiro && organization?.industry) {
                setBillingGiro(String(organization.industry).trim());
              }
            }}
            disabled={isClosing}
            style={inputStyle}
          >
            {BILLING_DOCUMENT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </Field>
        {billingDocumentType === 'Factura' ? (
          <>
            <Field muted={ui.muted} label="Dirección de facturación">
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <select value={billingAddress} onChange={(e) => setBillingAddress(e.target.value)} disabled={isClosing} style={{ ...inputStyle, flex: 1, minWidth: 0 }}>
                  <option value="">Seleccionar dirección…</option>
                  {billingAddress && !addressOptions.some((a) => a.value === billingAddress) ? (
                    <option value={billingAddress}>{billingAddress}</option>
                  ) : null}
                  {addressOptions.map((a) => (
                    <option key={`bill-${a.value}`} value={a.value}>{a.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={() => {
                    setNewAddressTarget('billing');
                    setShowNewBillingAddress((v) => !v);
                    setShowNewDeliveryAddress(false);
                  }}
                  disabled={isClosing}
                  title="Nueva dirección de facturación"
                  aria-label="Nueva dirección de facturación"
                  style={plusButtonStyle}
                >
                  <Plus size={16} />
                </button>
              </div>
            </Field>
            <Field muted={ui.muted} label="Giro">
              <input type="text" value={billingGiro} onChange={(e) => setBillingGiro(e.target.value)} disabled={isClosing} style={inputStyle} />
            </Field>
          </>
        ) : null}
      </div>
      {showNewBillingAddress && billingDocumentType === 'Factura' ? (
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <input style={{ ...inputStyle, flex: 1 }} placeholder="Nueva dirección" value={newAddressText} onChange={(e) => setNewAddressText(e.target.value)} />
          <button type="button" disabled={savingExtra} onClick={onSaveNewAddress} style={{ ...btnBase, border: 'none', background: '#2563eb', color: '#fff' }}>Guardar</button>
        </div>
      ) : null}
    </div>
  );

  const renderStep2 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: ui.muted }}>
        Paso 2 de 3 — Fechas y solicitud a producción
      </div>
      {sectionTitle('Fechas programadas')}
      <div style={grid3}>
        <Field muted={ui.muted} label="Fecha de despacho">
          <input type="date" value={dispatchDate} onChange={(e) => setDispatchDate(e.target.value)} disabled={isClosing} style={inputStyle} />
        </Field>
        <Field muted={ui.muted} label="Código entrega programada">
          <input
            type="text"
            value={scheduledDeliveryCode}
            onChange={(e) => setScheduledDeliveryCode(e.target.value.toUpperCase())}
            disabled={isClosing}
            autoComplete="off"
            placeholder="DLV0426-A"
            style={{
              ...inputStyle,
              borderColor: deliveryCodeError ? '#dc2626' : undefined,
            }}
          />
          {deliveryCodeError ? (
            <span style={{ fontSize: '11px', color: '#dc2626', lineHeight: 1.35 }}>{deliveryCodeError}</span>
          ) : (
            <span style={{ fontSize: '11px', color: ui.muted, lineHeight: 1.35 }}>
              Se creará una entrega logística con este código al confirmar el cierre.
            </span>
          )}
        </Field>
        <Field muted={ui.muted} label="Fecha de entrega">
          <input
            type="date"
            value={expectedDeliveryDate}
            readOnly
            disabled
            style={{ ...inputStyle, backgroundColor: ui.sectionBg, opacity: 0.85, cursor: 'not-allowed' }}
          />
        </Field>
        <div style={{ fontSize: '11px', color: ui.muted, alignSelf: 'end', lineHeight: 1.45, paddingBottom: '8px', gridColumn: '1 / -1' }}>
          La entrega se calcula sumando {Number(quotation?.delivery_days ?? 0)} días hábiles a la fecha de aceptación de OC
          {hasPurchaseOrder ? '' : ' (o fecha de cierre si no hay OC)'}.
        </div>
      </div>

      {sectionTitle('Solicitud a producción (T-COAT)')}
      <div style={{ fontSize: '12px', color: ui.muted, lineHeight: 1.45 }}>
        Selecciona los ítems y cantidades para producción. La fecha límite será la fecha de despacho programada.
        Usa «Omitir solicitud» abajo si no quieres crear la solicitud ahora.
      </div>
      {renderItemSelectionTable(
        production,
        productionSelections,
        updateProductionSelection,
        'No hay ítems T-COAT en esta cotización.'
      )}
      <Field muted={ui.muted} label="Notas para producción (opcional)">
        <textarea
          value={productionNotes}
          onChange={(e) => setProductionNotes?.(e.target.value)}
          disabled={isClosing}
          rows={3}
          placeholder="Indicaciones especiales, urgencia, color crítico, etc."
          style={{
            ...inputStyle,
            height: 'auto',
            minHeight: '72px',
            resize: 'vertical',
            paddingTop: '8px',
            paddingBottom: '8px',
          }}
        />
      </Field>
    </div>
  );

  const renderStep3 = () => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: ui.muted }}>
        Paso 3 de 3 — Solicitud a adquisiciones (upselling)
      </div>
      <div style={{ fontSize: '12px', color: ui.muted, lineHeight: 1.45 }}>
        Selecciona los ítems de upselling para solicitar a adquisiciones. La fecha límite será la fecha de despacho programada
        ({dispatchDate || '—'}). Usa «Omitir solicitud» abajo si no quieres crear la solicitud ahora.
      </div>
      {renderItemSelectionTable(
        upselling,
        upsellingSelections,
        updateUpsellingSelection,
        'No hay ítems de upselling en esta cotización.'
      )}
    </div>
  );

  if (vendidaWizardStep === 2) return renderStep2();
  if (vendidaWizardStep === 3) return renderStep3();
  return renderStep1();
};

export default QuotationVendidaCloseWizard;
